import { useState, useMemo, useCallback } from "react"
import { subscriptions_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import { calculateProratedAmount } from "./utils"
import type { CreditCardInfo, BillingInfo } from "./constants"

type UseAddSlotsFormProps = {
  currentEmailAccountsCount: number
  currentMaxEmailAccounts: number
  planIncludedEmailAccounts: number
  pricePerSlot: number
  nextBillingDate?: string | null
  billingPeriodStart?: string | null
  initialCardholderName?: string | null
  creditCardInfo?: CreditCardInfo | null
  billingCycle: string
  subscriptionLevel: number
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  onClose: () => void
}

type SaveCreditCardParams = {
  paymentMethodId: string
  cardholderName: string
}

export function useAddSlotsForm({
  currentEmailAccountsCount,
  currentMaxEmailAccounts,
  planIncludedEmailAccounts,
  pricePerSlot,
  nextBillingDate,
  billingPeriodStart,
  initialCardholderName,
  creditCardInfo,
  billingCycle,
  subscriptionLevel,
  onClose,
}: UseAddSlotsFormProps) {
  // Calculate current extra slots (beyond plan-included)
  const currentExtraSlots = Math.max(
    0,
    currentMaxEmailAccounts - planIncludedEmailAccounts,
  )

  // Minimum extra slots: user cannot go below the number of connected accounts
  // that exceed the plan-included amount
  // E.g., if plan includes 10 and user has 11 connected, minimum extra = 1
  // E.g., if plan includes 10 and user has 8 connected, minimum extra = 0
  const minExtraSlots = Math.max(
    0,
    currentEmailAccountsCount - planIncludedEmailAccounts,
  )

  // extraSlotsQuantity represents the number of extra slots (beyond plan-included)
  // Initialize to current extra slots + 1 (user wants to ADD slots)
  const [extraSlotsQuantity, setExtraSlotsQuantity] = useState(
    currentExtraSlots + 1,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditingCard, setIsEditingCard] = useState(false)
  const [cardholderName, setCardholderName] = useState(
    initialCardholderName || "",
  )
  const [currentCreditCard, setCurrentCreditCard] = useState(creditCardInfo)

  // Price is based on extra slots only (plan-included slots are free)
  // For yearly: show monthly equivalent (pricePerSlot)
  // For monthly: show monthly price (pricePerSlot)
  const totalPrice = extraSlotsQuantity * pricePerSlot

  // Calculate how many NEW extras are being added
  const newExtrasBeingAdded = Math.max(
    0,
    extraSlotsQuantity - currentExtraSlots,
  )

  // Calculate amount due today based on billing cycle:
  // - Yearly: annual price (pricePerSlot × 12) prorated for remaining time
  // - Monthly: just the monthly price, no proration
  const proratedAmount = useMemo(() => {
    if (billingCycle === "yearly") {
      // Annual price = monthly × 12, then prorate based on remaining time
      const annualPrice = pricePerSlot * 12
      return calculateProratedAmount(
        annualPrice,
        newExtrasBeingAdded,
        nextBillingDate,
        billingPeriodStart,
      )
    }
    // Monthly: just charge the monthly price
    return pricePerSlot * newExtrasBeingAdded
  }, [
    billingCycle,
    pricePerSlot,
    newExtrasBeingAdded,
    nextBillingDate,
    billingPeriodStart,
  ])

  const handleEditPaymentMethod = useCallback(() => {
    setIsEditingCard(true)
  }, [])

  const handleCancelEditCard = useCallback(() => {
    setIsEditingCard(false)
    setCardholderName(initialCardholderName || "")
  }, [initialCardholderName])

  const saveCreditCard = useCallback(
    async ({
      paymentMethodId,
      cardholderName,
    }: SaveCreditCardParams): Promise<boolean> => {
      try {
        const response = await fetch("/credit-cards/1", {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({
            payment_method_id: paymentMethodId,
            full_name: cardholderName,
          }),
          credentials: "same-origin",
        })

        const data = await response.json()

        if (!response.ok) {
          window.App?.displayFlash?.(
            data.error ||
              i18n.t("js.email_accounts_center.add_slots.card_error"),
            "error",
          )
          return false
        }

        return true
      } catch (error) {
        console.error("Failed to save credit card:", error)
        window.App?.displayFlash?.(
          i18n.t("js.email_accounts_center.add_slots.card_error"),
          "error",
        )
        return false
      }
    },
    [],
  )

  const updateSubscription = useCallback(
    async (totalDesiredSlots: number): Promise<boolean> => {
      try {
        // totalDesiredSlots is the total email account slots the user wants
        // The backend will calculate extras as: totalDesiredSlots - planIncludedEmailAccounts
        const response = await fetch(subscriptions_path(), {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({
            update_email_accounts: true,
            email_accounts: totalDesiredSlots,
            level: subscriptionLevel,
            billing_cycle: billingCycle,
          }),
          credentials: "same-origin",
        })

        const data = await response.json()

        if (data.success) {
          window.App?.displayFlash?.(
            i18n.t("js.email_accounts_center.add_slots.success"),
            "success",
          )
          return true
        } else {
          window.App?.displayFlash?.(
            data.error_message ||
              i18n.t("js.email_accounts_center.add_slots.error_generic"),
            "error",
          )
          return false
        }
      } catch (error) {
        console.error("Failed to update subscription:", error)
        window.App?.displayFlash?.(
          i18n.t("js.email_accounts_center.add_slots.error_generic"),
          "error",
        )
        return false
      }
    },
    [subscriptionLevel, billingCycle],
  )

  const handleSuccessfulSubmit = useCallback(() => {
    onClose()
    window.location.reload()
  }, [onClose])

  const isCardFormVisible = isEditingCard || !currentCreditCard

  return {
    // State
    extraSlotsQuantity,
    setExtraSlotsQuantity,
    isSubmitting,
    setIsSubmitting,
    isEditingCard,
    cardholderName,
    setCardholderName,
    currentCreditCard,
    setCurrentCreditCard,

    // Computed values
    totalPrice,
    proratedAmount,
    isCardFormVisible,
    minExtraSlots,
    planIncludedEmailAccounts,

    // Handlers
    handleEditPaymentMethod,
    handleCancelEditCard,
    saveCreditCard,
    updateSubscription,
    handleSuccessfulSubmit,
  }
}
